import L, {
  LeafletMouseEvent,
  LatLngExpression,
  Map,
  LeafletKeyboardEvent,
} from 'leaflet';
import { initialCenterWCC } from './constants';
import { shimmer } from '../mapUtils';

export enum ParksErrorMessage {
  RoyalParks = `<div style="background-color: #FFCCCC; padding: 10px; border-radius: 5px;">
    <span>This area is managed by The Royal Parks. Report an issue at <a href="https://www.royalparks.org.uk/get-in-touch">The Royal Parks website</a>.</span></div>`,
  PrivatePark = `<div style="background-color: #FFCCCC; padding: 10px; border-radius: 5px;">
    <span>This area is privately managed. Report issues directly to the owner.</span></div>`,
  MayorOfLondon = `<div style="background-color: #FFCCCC; padding: 10px; border-radius: 5px;">
    <span>This area is managed by the Mayor of London. Report issues at <a href="https://london.gov.uk/contact-us-form">The Mayor of London website</a>.</span></div>`,
  TfL = `<div style="background-color: #FFCCCC; padding: 10px; border-radius: 5px;">
    <span>This area is managed by Transport for London. Report issues at <a href="https://streetcare.tfl.gov.uk/">Transport for London's Streetcare</a>.</span></div>`,
  Parliament = `<div style="background-color: #FFCCCC; padding: 10px; border-radius: 5px;">
    <span>This area is managed by the Houses of Parliament. Report issues at <a href="https://www.parliament.uk/site-information/contact-us/">The Houses of Parliament website</a>.</span></div>`,
  CrownEstate = `<div style="background-color: #FFCCCC; padding: 10px; border-radius: 5px;">
    <span>This area is managed by the Crown Estate. Report issues at <a href="https://thecrownestate.co.uk/contact-us">The Crown Estate website</a>.</span></div>`,
  CanalRiverTrust = `<div style="background-color: #FFCCCC; padding: 10px; border-radius: 5px;">
    <span>This area is managed by the Canal and River Trust. Report issues at <a href="https://canalrivertrust.org.uk/contact-us">The Canal and River Trust website</a>.</span></div>`,
  UnableToFindAsset = `<div style="background-color: #FFCCCC; padding: 10px; border-radius: 5px;">
    <span>This location may be private land or not included in our mapped areas. Try again.</span></div>`,
}

export enum ParksOwner {
  WCC = 1,
  RoyalParks = 2,
  PrivatePark = 3,
  MayorOfLondon = 4,
  TfL = 5,
  Parliament = 6,
  CrownEstate = 7,
  CanalRiverTrust = 8,
}

export const loadingPopup = (event: LeafletMouseEvent, map: Map) => {
  L.popup({ closeOnClick: true })
    .setLatLng(event.latlng)
    .setContent(shimmer())
    .openOn(map);
};

export const parksErrorPopup = (
  event: LeafletMouseEvent['latlng'],
  map: Map,
  OwnerId: number | undefined,
) => {
  const popupLocation: LatLngExpression = event;
  if (!OwnerId) {
    L.popup({ closeOnClick: true })
      .setLatLng(popupLocation)
      .setContent(ParksErrorMessage.UnableToFindAsset)
      .openOn(map);
    return;
  }
  switch (OwnerId) {
    case ParksOwner.RoyalParks:
      L.popup({ closeOnClick: true })
        .setLatLng(popupLocation)
        .setContent(ParksErrorMessage.RoyalParks)
        .openOn(map);
      return;
    case ParksOwner.PrivatePark:
      L.popup({ closeOnClick: true })
        .setLatLng(popupLocation)
        .setContent(ParksErrorMessage.PrivatePark)
        .openOn(map);
      return;
    case ParksOwner.MayorOfLondon:
      L.popup({ closeOnClick: true })
        .setLatLng(popupLocation)
        .setContent(ParksErrorMessage.MayorOfLondon)
        .openOn(map);
      return;
    case ParksOwner.TfL:
      L.popup({ closeOnClick: true })
        .setLatLng(popupLocation)
        .setContent(ParksErrorMessage.TfL)
        .openOn(map);
      return;
    case ParksOwner.Parliament:
      L.popup({ closeOnClick: true })
        .setLatLng(popupLocation)
        .setContent(ParksErrorMessage.Parliament)
        .openOn(map);
      return;
    case ParksOwner.CrownEstate:
      L.popup({ closeOnClick: true })
        .setLatLng(popupLocation)
        .setContent(ParksErrorMessage.CrownEstate)
        .openOn(map);
      return;
    case ParksOwner.CanalRiverTrust:
      L.popup({ closeOnClick: true })
        .setLatLng(popupLocation)
        .setContent(ParksErrorMessage.CanalRiverTrust)
        .openOn(map);
      return;
    default:
      L.popup({ closeOnClick: true })
        .setLatLng(popupLocation)
        .setContent(ParksErrorMessage.UnableToFindAsset)
        .openOn(map);
      return;
  }
};

export const outsideBoroughPopup = (event: LeafletMouseEvent, map: Map) => {
  L.popup({ closeOnClick: true })
    .setLatLng(event.latlng)
    .setContent(
      `<div style="background-color: #FFCCCC; padding: 10px; border-radius: 5px;">
              <span>You selected a location outside the borough, try again</span></div>`,
    )
    .openOn(map);
};

export const errorZoomInPopup = (event: LeafletMouseEvent, map: Map) => {
  L.popup({ closeOnClick: true })
    .setLatLng(event.latlng)
    .setContent(
      `<div style="background-color: #FFCCCC; padding: 10px; border-radius: 5px;">
              <span>Zoom in closer to select a accurate location</span></div>`,
    )
    .openOn(map);
};

export const geolocationOutOfBoroughPopup = (map: Map) => {
  map.flyTo(initialCenterWCC, 13);
  L.popup({ closeOnClick: true })
    .setLatLng(initialCenterWCC)
    .setContent(
      `<div style="background-color: #FFCCCC; padding: 10px; border-radius: 5px;">
              <span>Your location needs to be inside the borough.</span></div>`,
    )
    .openOn(map);
};

export const tflBoundaryPopup = (event: LeafletMouseEvent, map: Map) => {
  L.popup({ closeOnClick: true })
    .setLatLng(event.latlng)
    .setContent(
      `<div style="background-color: #FFD420; padding: 10px; border-radius: 5px;">
              <span>Westminster City Council does not maintain this road. It is managed by Transport for London (TfL)</span></div>`,
    )
    .openOn(map);
};

/**
 * The error message is specified in the story, refer to here: {@link https://westminstergov.atlassian.net/browse/RITE-1248}
 */
export const wasteAddressErrorPopup = (event: LeafletMouseEvent, map: Map) => {
  L.popup({ closeOnClick: true })
    .setLatLng(event.latlng)
    .setContent(
      `<div style="background-color: #FFCCCC; padding: 10px; border-radius: 5px;">
              <span>We cannot retrieve an address and may not provide waste related services for this location. If you want to try again, please select a different location.</span></div>`,
    )
    .openOn(map);
};

export const addressErrorPopup = (
  map: Map,
  event?: LeafletMouseEvent | LeafletKeyboardEvent,
) => {
  const popupLocation: LatLngExpression =
    (event as LeafletMouseEvent).latlng ||
    event?.propagatedFrom.feature?.geometry ||
    initialCenterWCC;
  const popupContent = `<div style="background-color: #FFCCCC; padding: 10px; border-radius: 5px;">
              <span>This location may be private land or not included in our mapped areas. Try again.</span></div>`;

  L.popup({ closeOnClick: true })
    .setLatLng(popupLocation)
    .setContent(popupContent)
    .openOn(map);
};
